import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { parseCURP, edadEnFecha, isValidCURP } from "@/lib/curp";
import { EntidadMunicipioSelect } from "@/components/EntidadMunicipioSelect";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserPlus, ArrowLeft, FileText, Upload, UserMinus, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PAISES_REGIONES } from "@/lib/paisesRegiones";

const DISCAPACIDADES = [
  "", "CEGUERA", "BAJA VISIÓN", "SORDERA", "HIPOACUSIA", "SORDOCEGUERA",
  "MOTRIZ", "INTELECTUAL", "PSICOSOCIAL", "MÚLTIPLE",
];
const NEURO = ["", "TEA (Autismo)", "TDAH", "Otro trastorno"];

const emptyForm = {
  curp: "", nombre: "", apellido_paterno: "", apellido_materno: "",
  fecha_nacimiento: "", genero: "H",
  lengua_indigena: false, nacido_fuera_mexico: false, pais_origen: "",
  escuela_procedencia_otro_pais: false,
  entidad_residencia: "Tamaulipas", municipio_residencia: "Tampico", afro_descendiente: false,
  discapacidad: "", neurodivergencia: "", aptitudes_sobresalientes: false,
  beca: false, tipo_beca: "",
  proviene_otra_escuela: false, origen_escuela: "",
  // Campos académicos 911 (Primaria/Secundaria)
  repetidor: false, atendido_usaer: false,
  reprobado_ciclo_pasado: false, asignaturas_reprobadas: 0,
  regularizado: false, materias_pendientes: 0,
  tipo_escuela_previa: "GENERAL", // GENERAL | INDIGENA | COMUNITARIA
  anios_preescolar_cursados: 0, // 0-3 para 1° primaria
  // Contacto y tutores (hoja "Datos Generales")
  nombre_mama: "", telefono_mama: "",
  nombre_papa: "", telefono_papa: "",
  domicilio: "",
};

export default function Alumnos() {
  const { grupoId } = useParams();
  const { user, fechaCorteEdad } = useAuth();
  const readOnly = ["jefe_sector", "supervisor_zona"].includes(user?.role);
  const [grupo, setGrupo] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [showBajas, setShowBajas] = useState(false);
  const [bajaFor, setBajaFor] = useState(null);
  const [motivoBaja, setMotivoBaja] = useState("");
  const [curpDup, setCurpDup] = useState(null);
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/alumnos/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_alumnos.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/alumnos/import?grupo_id=${grupoId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errs = data.errors?.length || 0;
      if (data.inserted > 0) {
        toast.success(`${data.inserted} alumno(s) importado(s)${errs ? ` · ${errs} error(es)` : ""}`);
      } else {
        toast.error(`Ningún registro importado${errs ? ` · ${errs} error(es)` : ""}`);
      }
      if (errs) {
        console.warn("Errores de importación:", data.errors);
        const previewErr = data.errors.slice(0, 5).map((x) => `Fila ${x.row}: ${x.error}`).join("\n");
        toast.message("Errores detectados", { description: previewErr });
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); }
  };

  const load = async () => {
    try {
      const [g, a] = await Promise.all([
        api.get(`/grupos/${grupoId}`),
        api.get("/alumnos", { params: { grupo_id: grupoId, include_bajas: showBajas } }),
      ]);
      setGrupo(g.data);
      setAlumnos(a.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [grupoId, showBajas]);

  // Debounced CURP duplicate check
  useEffect(() => {
    const curp = (form.curp || "").toUpperCase();
    if (curp.length < 18) { setCurpDup(null); return; }
    const t = setTimeout(async () => {
      try {
        const params = { curp };
        if (editing?.id) params.exclude_id = editing.id;
        const { data } = await api.get("/alumnos/check-curp", { params });
        setCurpDup(data.exists ? data.alumno : null);
      } catch { setCurpDup(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.curp, editing?.id]);

  const onCurpChange = (curp) => {
    const parsed = parseCURP(curp);
    setForm((f) => ({
      ...f,
      curp: curp.toUpperCase(),
      fecha_nacimiento: parsed?.fecha_nacimiento || f.fecha_nacimiento,
      genero: parsed?.genero || f.genero,
    }));
  };

  const startCreate = () => { setEditing(null); setForm(emptyForm); setCurpDup(null); setOpen(true); };
  const startEdit = (a) => {
    setEditing(a);
    setForm({ ...emptyForm, ...a });
    setCurpDup(null);
    setOpen(true);
  };

  const confirmBaja = async () => {
    if (!bajaFor || !motivoBaja.trim()) return;
    try {
      await api.post(`/alumnos/${bajaFor.id}/baja`, { motivo: motivoBaja.trim() });
      toast.success(`${bajaFor.nombre} dado(a) de baja`);
      setBajaFor(null);
      setMotivoBaja("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reactivar = async (a) => {
    try {
      await api.post(`/alumnos/${a.id}/reactivar`);
      toast.success(`${a.nombre} reactivado(a)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.curp && !isValidCURP(form.curp)) {
      toast.error("La CURP del alumno debe tener 18 caracteres válidos.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/alumnos/${editing.id}`, form);
        toast.success("Alumno actualizado");
      } else {
        await api.post("/alumnos", { ...form, grupo_id: grupoId });
        toast.success("Alumno registrado");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/alumnos/${confirm.id}`);
      toast.success("Alumno eliminado");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return alumnos.filter((a) =>
      !s || `${a.nombre} ${a.apellido_paterno} ${a.apellido_materno} ${a.curp}`.toLowerCase().includes(s)
    );
  }, [alumnos, search]);

  const backTo = user?.role === "admin" ? "/admin/grupos" : "/docente";

  return (
    <AppLayout>
      <div className="max-w-7xl">
        <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-[#545454] hover:text-[#235B4E] mb-3">
          <ArrowLeft className="w-4 h-4" /> Regresar
        </Link>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">
              {grupo?.plantel?.nivel} · {grupo?.plantel?.nombre}
            </div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Alumnos · {grupo?.grado}° {grupo?.letra}
            </h1>
            <p className="text-sm text-[#545454] mt-1">Ciclo {grupo?.ciclo}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={onImportFile}
              className="hidden"
              data-testid="csv-file-input"
            />
            <Button
              variant="outline"
              onClick={downloadTemplate}
              data-testid="btn-download-template"
            >
              Plantilla Excel
            </Button>
            <Button
              variant="outline"
              disabled={importing || readOnly}
              onClick={() => fileInputRef.current?.click()}
              data-testid="btn-import-csv"
            >
              <Upload className="w-4 h-4 mr-1" /> {importing ? "Importando…" : "Importar Excel/CSV"}
            </Button>
            {user?.role === "admin" && grupo && (
              <Link to={`/admin/reportes?plantel=${grupo.plantel_id}`}>
                <Button variant="outline" data-testid="btn-goto-reporte">
                  <FileText className="w-4 h-4 mr-1" /> Reporte 911
                </Button>
              </Link>
            )}
            {!readOnly && (
              <Button data-testid="btn-add-alumno" onClick={startCreate} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
                <UserPlus className="w-4 h-4 mr-1" /> Nuevo alumno
              </Button>
            )}
          </div>
        </div>

        <div className="mb-4 max-w-sm">
          <Input
            data-testid="search-alumno"
            placeholder="Buscar por nombre o CURP…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead>CURP</TableHead>
                <TableHead>Apellido paterno</TableHead>
                <TableHead>Apellido materno</TableHead>
                <TableHead>Nombre(s)</TableHead>
                <TableHead>Género</TableHead>
                <TableHead>F. nacimiento</TableHead>
                <TableHead>Edad al 1° sep</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#545454]">
                  Sin alumnos registrados
                </TableCell></TableRow>
              ) : filtered.map((a) => (
                <TableRow
                  key={a.id}
                  data-testid={`alumno-row-${a.id}`}
                  className={a.activo === false ? "bg-[#9F2241]/5 text-[#6B7280]" : ""}
                >
                  <TableCell className="font-mono text-xs">
                    {a.curp}
                    {a.activo === false && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#9F2241]/15 text-[#9F2241] uppercase tracking-wider">
                        Baja
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{a.apellido_paterno}</TableCell>
                  <TableCell>{a.apellido_materno || "—"}</TableCell>
                  <TableCell className="font-medium">{a.nombre}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded ${a.genero === "H" ? "bg-[#235B4E]/10 text-[#235B4E]" : "bg-[#9F2241]/10 text-[#9F2241]"}`}>
                      {a.genero === "H" ? "Hombre" : "Mujer"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{a.fecha_nacimiento}</TableCell>
                  <TableCell className="font-semibold">{edadEnFecha(a.fecha_nacimiento, fechaCorteEdad(grupo?.plantel?.nivel))}</TableCell>
                  <TableCell className="text-right">
                    {readOnly ? (
                      <span className="text-[10px] text-[#9CA3AF] italic">Solo lectura</span>
                    ) : a.activo === false ? (
                      user?.role !== "docente" ? (
                        <Button
                          data-testid={`btn-reactivar-${a.id}`}
                          variant="ghost" size="sm"
                          onClick={() => reactivar(a)}
                          title={`Motivo: ${a.motivo_baja || "—"}`}
                        >
                          <UserPlus className="w-4 h-4 text-[#235B4E]" />
                        </Button>
                      ) : (
                        <span className="text-[10px] text-[#9F2241] italic" title={`Motivo: ${a.motivo_baja || "—"}`}>Dado de baja</span>
                      )
                    ) : (
                      <>
                        <Button data-testid={`btn-edit-a-${a.id}`} variant="ghost" size="sm" onClick={() => startEdit(a)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {user?.role !== "docente" && (
                          <Button
                            data-testid={`btn-baja-a-${a.id}`}
                            variant="ghost" size="sm"
                            onClick={() => { setBajaFor(a); setMotivoBaja(""); }}
                            title="Dar de baja"
                          >
                            <UserMinus className="w-4 h-4 text-[#BC955C]" />
                          </Button>
                        )}
                        <Button data-testid={`btn-delete-a-${a.id}`} variant="ghost" size="sm" onClick={() => setConfirm(a)}>
                          <Trash2 className="w-4 h-4 text-[#9F2241]" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar alumno" : "Nuevo alumno"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <section>
              <SectionTitle>Datos básicos</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-3 space-y-1.5">
                  <Label className="text-xs">CURP *</Label>
                  <Input
                    data-testid="alumno-curp"
                    value={form.curp}
                    onChange={(e) => onCurpChange(e.target.value)}
                    placeholder="18 caracteres"
                    maxLength={18}
                    required
                    className="font-mono uppercase tracking-wider"
                  />
                  {form.curp && form.curp.length > 0 && !isValidCURP(form.curp) ? (
                    <p className="text-xs text-[#9F2241]" data-testid="alumno-curp-error">
                      Formato de CURP inválido. Debe tener 18 caracteres: 4 letras + 6 dígitos (AAMMDD) + H/M + 2 letras + 3 letras + 2 alfanuméricos.
                    </p>
                  ) : (
                    <p className="text-xs text-[#9CA3AF]">
                      Se detectará automáticamente la fecha de nacimiento y el género.
                    </p>
                  )}
                  {curpDup && (
                    <div className="flex items-start gap-2 p-2 rounded border border-[#BC955C]/50 bg-[#BC955C]/10 text-xs" data-testid="curp-duplicate-warn">
                      <AlertTriangle className="w-4 h-4 text-[#BC955C] shrink-0 mt-0.5" />
                      <div className="text-[#374151]">
                        <div className="font-semibold text-[#10312B]">
                          CURP ya registrada
                          {curpDup.activo === false && <span className="text-[#9F2241] ml-1">(baja)</span>}
                        </div>
                        <div>
                          {curpDup.apellido_paterno} {curpDup.apellido_materno} {curpDup.nombre}
                        </div>
                        {curpDup.plantel && (
                          <div className="text-[#6B7280]">
                            {curpDup.plantel.nombre} · {curpDup.grupo?.grado}° {curpDup.grupo?.letra} ({curpDup.grupo?.ciclo})
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Apellido paterno *</Label>
                  <Input data-testid="alumno-ap" required value={form.apellido_paterno}
                         onChange={(e) => setForm({ ...form, apellido_paterno: e.target.value.toUpperCase() })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Apellido materno</Label>
                  <Input value={form.apellido_materno}
                         onChange={(e) => setForm({ ...form, apellido_materno: e.target.value.toUpperCase() })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nombre(s) *</Label>
                  <Input data-testid="alumno-nombre" required value={form.nombre}
                         onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fecha de nacimiento *</Label>
                  <Input type="date" required value={form.fecha_nacimiento}
                         readOnly={form.curp.length === 18}
                         data-testid="alumno-fecha-nac"
                         className={form.curp.length === 18 ? "bg-[#F3F4F6] cursor-not-allowed" : ""}
                         onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} />
                  {form.curp.length === 18 && (
                    <p className="text-[10px] text-[#9CA3AF]">Se toma de la CURP.</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Género *</Label>
                  <Select value={form.genero}
                          disabled={form.curp.length === 18}
                          onValueChange={(v) => setForm({ ...form, genero: v })}>
                    <SelectTrigger data-testid="alumno-genero" className={form.curp.length === 18 ? "bg-[#F3F4F6] cursor-not-allowed" : ""}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="H">Hombre</SelectItem>
                      <SelectItem value="M">Mujer</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.curp.length === 18 && (
                    <p className="text-[10px] text-[#9CA3AF]">Se toma de la CURP.</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Edad al {fechaCorteEdad(grupo?.plantel?.nivel)}</Label>
                  <Input readOnly data-testid="alumno-edad" value={edadEnFecha(form.fecha_nacimiento, fechaCorteEdad(grupo?.plantel?.nivel)) || ""} className="bg-[#F3F4F6] cursor-not-allowed" />
                  <p className="text-[10px] text-[#9CA3AF]">Años cumplidos a la fecha de corte configurada por Superadmin.</p>
                </div>
              </div>
            </section>

            <section>
              <SectionTitle>Características</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Cb label="Habla lengua indígena" checked={form.lengua_indigena}
                    onChange={(v) => setForm({ ...form, lengua_indigena: v })} testId="cb-indigena" />
                <Cb label="Afro-mexicano / Afro-descendiente" checked={form.afro_descendiente}
                    onChange={(v) => setForm({ ...form, afro_descendiente: v })} testId="cb-afro" />
                <Cb label="Nacido fuera de México" checked={form.nacido_fuera_mexico}
                    onChange={(v) => setForm({ ...form, nacido_fuera_mexico: v })} testId="cb-fuera" />
                <Cb label="Aptitudes sobresalientes" checked={form.aptitudes_sobresalientes}
                    onChange={(v) => setForm({ ...form, aptitudes_sobresalientes: v })} testId="cb-aptitudes" />
                <Cb label="Escuela anterior en el extranjero" checked={form.escuela_procedencia_otro_pais}
                    onChange={(v) => setForm({ ...form, escuela_procedencia_otro_pais: v })} testId="cb-escuela-extranjero" />
                {(form.nacido_fuera_mexico || form.escuela_procedencia_otro_pais) && (
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-xs">País / Región de origen (Formato 911)</Label>
                    <Select
                      value={form.pais_origen || "__none__"}
                      onValueChange={(v) => setForm({ ...form, pais_origen: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="pais-origen"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Sin especificar —</SelectItem>
                        {PAISES_REGIONES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <EntidadMunicipioSelect
                  entidad={form.entidad_residencia}
                  municipio={form.municipio_residencia}
                  onChange={({ entidad, municipio }) =>
                    setForm({ ...form, entidad_residencia: entidad, municipio_residencia: municipio })
                  }
                  idPrefix="alumno"
                  entidadLabel="Entidad de residencia"
                  municipioLabel="Municipio de residencia"
                />
                <div className="space-y-1.5">
                  <Label className="text-xs">Discapacidad</Label>
                  <Select value={form.discapacidad || "__none__"} onValueChange={(v) => setForm({ ...form, discapacidad: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Ninguna" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Ninguna</SelectItem>
                      {DISCAPACIDADES.filter(Boolean).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Neurodivergencia / Trastorno</Label>
                  <Select value={form.neurodivergencia || "__none__"} onValueChange={(v) => setForm({ ...form, neurodivergencia: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Ninguna" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Ninguna</SelectItem>
                      {NEURO.filter(Boolean).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section>
              <SectionTitle>Tutores y contacto</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nombre de la mamá (tutor)</Label>
                  <Input data-testid="alumno-mama"
                         value={form.nombre_mama}
                         onChange={(e) => setForm({ ...form, nombre_mama: e.target.value.toUpperCase() })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Teléfono mamá</Label>
                  <Input data-testid="alumno-tel-mama"
                         value={form.telefono_mama}
                         onChange={(e) => setForm({ ...form, telefono_mama: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nombre del papá (tutor)</Label>
                  <Input data-testid="alumno-papa"
                         value={form.nombre_papa}
                         onChange={(e) => setForm({ ...form, nombre_papa: e.target.value.toUpperCase() })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Teléfono papá</Label>
                  <Input data-testid="alumno-tel-papa"
                         value={form.telefono_papa}
                         onChange={(e) => setForm({ ...form, telefono_papa: e.target.value })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs">Domicilio</Label>
                  <Input data-testid="alumno-domicilio"
                         value={form.domicilio}
                         onChange={(e) => setForm({ ...form, domicilio: e.target.value.toUpperCase() })}
                         placeholder="Calle, número, colonia, CP, municipio" />
                </div>
              </div>
            </section>

            <section>
              <SectionTitle>Beca y procedencia</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Cb label="Cuenta con beca" checked={form.beca}
                    onChange={(v) => setForm({ ...form, beca: v })} testId="cb-beca" />
                {form.beca && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tipo de beca</Label>
                    <Select value={form.tipo_beca || "OTRA"} onValueChange={(v) => setForm({ ...form, tipo_beca: v })}>
                      <SelectTrigger data-testid="tipo-beca"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RITA_CETINA">Beca Rita Cetina (federal)</SelectItem>
                        <SelectItem value="FEDERAL_OTRA">Otra beca federal</SelectItem>
                        <SelectItem value="ESTATAL">Estatal</SelectItem>
                        <SelectItem value="MUNICIPAL">Municipal</SelectItem>
                        <SelectItem value="FUNDACION">Fundación / asociación civil</SelectItem>
                        <SelectItem value="PROPIA">De la propia escuela</SelectItem>
                        <SelectItem value="PARTICULAR">Particular</SelectItem>
                        <SelectItem value="OTRA">Otra</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Cb label="Proviene de otra escuela" checked={form.proviene_otra_escuela}
                    onChange={(v) => setForm({ ...form, proviene_otra_escuela: v })} testId="cb-proviene" />
                {form.proviene_otra_escuela && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Origen escuela previa</Label>
                    <Select value={form.origen_escuela || "MISMA_ENTIDAD"} onValueChange={(v) => setForm({ ...form, origen_escuela: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MISMA_ENTIDAD">Misma entidad</SelectItem>
                        <SelectItem value="OTRA_ENTIDAD">Otra entidad</SelectItem>
                        <SelectItem value="OTRO_PAIS">Otro país</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </section>

            <section>
              <SectionTitle>Datos académicos (911)</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Situación 911 *</Label>
                  <div className="flex gap-4 pt-1">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="situacion911"
                        data-testid="radio-nuevo-ingreso"
                        checked={!form.repetidor}
                        onChange={() => setForm({ ...form, repetidor: false })}
                      />
                      <span>Nuevo ingreso</span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="situacion911"
                        data-testid="radio-repetidor"
                        checked={!!form.repetidor}
                        onChange={() => setForm({ ...form, repetidor: true })}
                      />
                      <span>Repetidor(a)</span>
                    </label>
                  </div>
                </div>
                <Cb label="Atendido por USAER / CAM" checked={form.atendido_usaer}
                    onChange={(v) => setForm({ ...form, atendido_usaer: v })} testId="cb-usaer" />
                {grupo?.plantel?.nivel === "SECUNDARIA" && (
                  <>
                    <Cb label="Reprobó una o más asignaturas el ciclo pasado" checked={form.reprobado_ciclo_pasado}
                        onChange={(v) => setForm({ ...form, reprobado_ciclo_pasado: v })} testId="cb-reprobado" />
                    {form.reprobado_ciclo_pasado && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Número de asignaturas reprobadas</Label>
                        <Input type="number" min="0" data-testid="alumno-asig-reprob"
                               value={form.asignaturas_reprobadas ?? 0}
                               onChange={(e) => setForm({ ...form, asignaturas_reprobadas: parseInt(e.target.value || "0", 10) })} />
                      </div>
                    )}
                    <Cb label="Regularizado (aprobó las asignaturas pendientes)" checked={form.regularizado}
                        onChange={(v) => setForm({ ...form, regularizado: v })} testId="cb-regularizado" />
                    <div className="space-y-1.5">
                      <Label className="text-xs">Materias pendientes (irregular)</Label>
                      <Input type="number" min="0" data-testid="alumno-materias-pend"
                             value={form.materias_pendientes ?? 0}
                             onChange={(e) => setForm({ ...form, materias_pendientes: parseInt(e.target.value || "0", 10) })} />
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label className="text-xs">Tipo de primaria de origen</Label>
                      <Select value={form.tipo_escuela_previa || "__none__"} onValueChange={(v) => setForm({ ...form, tipo_escuela_previa: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="alumno-tipo-previa"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin especificar —</SelectItem>
                          <SelectItem value="GENERAL">Primaria General</SelectItem>
                          <SelectItem value="INDIGENA">Primaria Indígena</SelectItem>
                          <SelectItem value="COMUNITARIA">Primaria Comunitaria</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {grupo?.plantel?.nivel === "PRIMARIA" && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Años de preescolar cursados</Label>
                      <Select value={String(form.anios_preescolar_cursados ?? 0)} onValueChange={(v) => setForm({ ...form, anios_preescolar_cursados: parseInt(v, 10) })}>
                        <SelectTrigger data-testid="alumno-anios-pre"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Ninguno</SelectItem>
                          <SelectItem value="1">1 año</SelectItem>
                          <SelectItem value="2">2 años</SelectItem>
                          <SelectItem value="3">3 años</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Tipo de preescolar de origen</Label>
                      <Select value={form.tipo_escuela_previa || "__none__"} onValueChange={(v) => setForm({ ...form, tipo_escuela_previa: v === "__none__" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin especificar —</SelectItem>
                          <SelectItem value="GENERAL">Preescolar General</SelectItem>
                          <SelectItem value="INDIGENA">Preescolar Indígena</SelectItem>
                          <SelectItem value="COMUNITARIA">Preescolar Comunitaria</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            </section>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-alumno" type="submit" disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar alumno?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el registro de {confirm?.nombre} {confirm?.apellido_paterno}. Esta acción es irreversible.
              Considere &quot;Dar de baja&quot; en su lugar para mantener el historial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#9F2241] hover:bg-[#801C34]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!bajaFor} onOpenChange={(o) => !o && setBajaFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dar de baja: {bajaFor?.nombre} {bajaFor?.apellido_paterno}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#545454]">
              El alumno se conservará en el historial del grupo pero no contará en los reportes 911.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Motivo de la baja *</Label>
              <Select value={motivoBaja} onValueChange={setMotivoBaja}>
                <SelectTrigger data-testid="motivo-baja"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CAMBIO_DE_ESCUELA">Cambio de escuela</SelectItem>
                  <SelectItem value="CAMBIO_DE_ENTIDAD">Cambio de entidad</SelectItem>
                  <SelectItem value="ABANDONO">Abandono escolar</SelectItem>
                  <SelectItem value="ENFERMEDAD">Enfermedad</SelectItem>
                  <SelectItem value="FALLECIMIENTO">Fallecimiento</SelectItem>
                  <SelectItem value="OTRO">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBajaFor(null)}>Cancelar</Button>
              <Button
                data-testid="btn-confirm-baja"
                onClick={confirmBaja}
                disabled={!motivoBaja}
                className="bg-[#BC955C] hover:bg-[#A6824D] text-white"
              >
                Confirmar baja
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-2 pb-1 border-b border-[#E5E7EB]">
      <div className="text-xs font-semibold uppercase tracking-wider text-[#235B4E]">{children}</div>
    </div>
  );
}

function Cb({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer">
      <Checkbox data-testid={testId} checked={checked} onCheckedChange={onChange} />
      <span className="text-sm text-[#374151]">{label}</span>
    </label>
  );
}
